/**
 * Step 3 of the solo final, "Your calls": the forecast tally, each recorded expectation as a table row (year, measure, what you expected, what happened),
 * the lesson of each year by the card that arrived, and one question for each call that went the other way. A record to learn from; it never changes the score.
 */
import type { RoundReport, SoloDebrief } from "@bsim/contracts";
import { yearLesson } from "../results/roundLessons";
import { GxCard } from "../theme/primitives";
import { GxTable } from "../theme/GxTable";
import { GxNotes } from "../theme/GxNotes";
import { figure } from "../theme/figures";
import { DIRECTION_DEFINITION } from "../format/basis";
import { callBasisLine, callRows, callsTally, forecastCalls, forecastTotals, wrongCallQuestion } from "./finale";

/** The card that arrived in a year (not a cancelled one): the one the call was written on when it names one, else the first. */
function cardOf(report: RoundReport | undefined, eventInstanceId: string | null): { readonly title: string } | null {
  const arrived = report?.events.filter((e) => e.status !== "cancelled") ?? [];
  const card = arrived.find((e) => e.eventInstanceId === eventInstanceId) ?? arrived[0];
  return card === undefined ? null : { title: card.card.title };
}

export function CallsStage({ debrief, reports }: { readonly debrief: SoloDebrief; readonly reports: readonly RoundReport[] }) {
  const rows = callRows(debrief);
  const tally = callsTally(debrief);
  const forecasts = forecastCalls(reports);
  const totals = forecastTotals(forecasts);
  const years = [...new Set(rows.map((r) => r.roundNo))];
  const report = (roundNo: number): RoundReport | undefined => reports.find((r) => r.roundNo === roundNo);
  const lessons = years.flatMap((y) => { const r = report(y); const l = r === undefined ? null : yearLesson(r); return l === null ? [] : [{ year: y, text: l }]; });
  const wrong = rows.filter((r) => r.verdict === "opposite");
  return (
    <GxCard title="Your calls, year by year" testId="solo.stage.calls">
      <div className="gx-card-b gx-final-calls">
        {forecasts.length === 0 ? null : (
          <section className="gx-final-calls-sec" data-testid="solo.calls.forecast-section">
            <h3 className="gx-eyebrow">Your forecasts</h3>
            <p data-testid="solo.calls.forecasts">{totals.sentence}</p>
            <GxTable testId="solo.calls.forecast-table" caption="Your yearly forecasts: what you called, what happened, and the two tallies"
              columns={[{ key: "measure", label: "Measure" }, { key: "year", label: "Year", kind: "number" }, { key: "call", label: "Your call" }, { key: "happened", label: "No-change plan, move" }, { key: "dir", label: "Right direction?" }, { key: "closer", label: "Closer than last year?" }]}
              rows={[
                ...forecasts.map((c, i) => ({
                  key: `${c.kpi}.${c.roundNo}`,
                  cells: {
                    measure: i === 0 || forecasts[i - 1]?.kpi !== c.kpi ? c.measure : <span className="gx-faint">{c.measure}</span>, year: figure(c.roundNo, "count"), call: c.yourCall, happened: c.happened,
                    dir: c.rightDirection === null ? "–" : c.rightDirection ? "Yes" : <b>No</b>, closer: c.closer ? "Yes" : <b>No</b>,
                  },
                })),
                { key: "totals", kind: "total" as const, testId: "solo.calls.forecast-totals", cells: { measure: `Out of ${totals.n}`, dir: `${totals.direction} of ${totals.n}`, closer: `${totals.closer} of ${totals.n}` } },
              ]} />
            <GxNotes notes={[DIRECTION_DEFINITION, "No-change plan, move is where that plan ended less last year’s figure, the basis every forecast is graded on. “Closer than last year” means closer to the actual than last year’s figure was."]} />
          </section>
        )}
        <section className="gx-final-calls-sec">
        <h3 className="gx-eyebrow">Expectations you recorded beside your decisions and cards</h3>
        <GxTable testId="solo.calls.table" caption="The expectations you recorded, and what happened"
          columns={[{ key: "year", label: "Year", kind: "number", width: "5rem" }, { key: "measure", label: "Measure" }, { key: "expected", label: "You expected" }, { key: "happened", label: "What happened" }]}
          rows={rows.map((r) => ({ key: r.key, testId: `solo.calls.row.${r.roundNo}`, cells: { year: figure(r.roundNo, "count"), measure: r.measure, expected: r.expected, happened: (() => { const b = callBasisLine(r); const h = r.verdict === "opposite" ? <b>{r.happened}</b> : r.happened; return b === null ? h : <>{h}<small className="gx-terminal-why gx-faint" data-testid="solo.calls.basis">{b}</small></>; })() } }))} />
        {tally === null ? null : <p data-testid="solo.calls.tally">{tally}</p>}
        </section>
        {lessons.length === 0 ? null : (
          <section data-testid="solo.calls.lessons">
            <h3 className="gx-eyebrow">What each year was meant to teach</h3>
            <ul className="gx-final-calls-list">{lessons.map((l) => <li key={l.year}><b>Year {l.year}.</b> {l.text}</li>)}</ul>
          </section>
        )}
        {wrong.length === 0 ? null : (
          <section data-testid="solo.calls.questions">
            <h3 className="gx-eyebrow">Worth asking</h3>
            <ul className="gx-final-calls-list">{wrong.map((r) => <li key={r.key}>{wrongCallQuestion(r, cardOf(report(r.roundNo), r.eventInstanceId)?.title ?? null)}</li>)}</ul>
          </section>
        )}
        <GxNotes notes={["This is a record of what you expected, to learn from. It does not change your score."]} />
      </div>
    </GxCard>
  );
}
